import { FC, useMemo, useState } from 'react';
import Spacing from 'components/Spacing';
import { localizedDate } from 'helpers/date';
import { useAppSelector } from 'helpers/hooks';
import { getUserData } from 'selectors/user';
import { getTransactionsData, getTransactionsSummary } from 'selectors/transactions';
import { getContributionSummaryList } from 'selectors/contributionSummary';
import { formatNumberAsCurrency, getUrlParams } from 'helpers/utils';
import { useGetShoppingCartSearchConfigQuery } from 'services/shoppingCartApi';
import { trackEvent } from 'helpers/mixpanelEvents';
import { useGateValue } from '@statsig/react-bindings';
import { SHOW_GIVING_HISTORY_DOWNLOAD_GATE } from 'constants/statsig';
import { Link } from 'components/Link';
import { UtilityText } from 'components/Typography';
import theme from 'config/theme';
import { downloadGivingHistory } from 'store/giving_history/actions';
import { RowContainer } from './styled';
import { TableSection } from './TableSection';

// Oath's shopping-cart donate page runs on client 100 (matches CartScreen).
const OATH_CLIENT_ID = 100;

// Attribution ref for checkouts started from the donation-history "Donate Now"
// button. Any inbound urlRef is preserved as a prefix.
const DONATION_HISTORY_REF = 'profile-donation-history';

const DEFAULT_ROW_COUNT = 4;

const getStatusDisplayLabel = (status: string): string => {
  const s = status?.trim().toLowerCase() ?? '';
  if (s === 'processing' || s === 'completed') return 'Completed';
  if (s === 'returned') return 'Returned';
  if (s === 'failed') return 'Failed';
  return status?.trim() || '—';
};

const columns = [
  {
    key: 'transaction_date',
    name: 'Date',
    formatFn: date => {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
    width: '33.33%',
  },
  {
    key: 'recipient',
    name: 'Recipient',
  },
  {
    key: 'page_name',
    name: 'Page',
  },
  {
    key: 'transaction_status',
    name: 'Donation Status',
    formatFn: (value: string) => getStatusDisplayLabel(value),
    className: 'align-left',
  },
  {
    key: 'amount',
    name: 'Amount Donated',
    formatFn: amount => formatNumberAsCurrency(amount, { trailingZeroDisplay: 'auto' }),
  },
];

const columnsMobile = [
  {
    key: 'transaction_date',
    name: 'Date',
    formatFn: date => {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'numeric',
        year: '2-digit',
      });
    },
    width: '15%',
  },
  {
    key: 'recipient',
    name: 'Recipient',
  },
  {
    key: 'amount',
    name: 'Amount',
    formatFn: amount => formatNumberAsCurrency(amount, { trailingZeroDisplay: 'auto' }),
  },
];

// "This Cycle" per-recipient table. election_category / display_info may be null
// — CustomTable renders '-' for empty cells, so no formatFn is needed there.
const cycleColumns = [
  {
    key: 'recipient_name',
    name: 'Recipient',
  },
  {
    key: 'election_category',
    name: 'Election Type',
  },
  {
    key: 'display_info',
    name: 'Election Details',
  },
  {
    key: 'cycle_total',
    name: 'Donations',
    formatFn: amount => formatNumberAsCurrency(amount, { trailingZeroDisplay: 'auto' }),
  },
];

const cycleColumnsMobile = [
  {
    key: 'recipient_name',
    name: 'Recipient',
  },
  {
    key: 'cycle_total',
    name: 'Donations',
    formatFn: amount => formatNumberAsCurrency(amount, { trailingZeroDisplay: 'auto' }),
  },
];

export type DonationHistoryTab = 'byDonation' | 'thisCycle';

interface Props {
  activeTab: DonationHistoryTab;
  onTabChange: (tab: DonationHistoryTab) => void;
}

// Tab state lives in the parent so the profile top card can deep-link into a tab.
export const DonationHistory: FC<Props> = ({ activeTab, onTabChange }) => {
  const showGivingHistoryDownload = useGateValue(SHOW_GIVING_HISTORY_DOWNLOAD_GATE);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const { userData, summary, transactions, contributionList } = useAppSelector(state => ({
    userData: getUserData(state),
    summary: getTransactionsSummary(state),
    transactions: getTransactionsData(state),
    contributionList: getContributionSummaryList(state),
  }));

  const { data: shoppingCartSearchConfig } = useGetShoppingCartSearchConfigQuery({
    client_id: OATH_CLIENT_ID,
  });
  const cartQueryString = shoppingCartSearchConfig?.query_string || '';

  const { urlRef } = getUrlParams();
  const donateRef = urlRef ? `${urlRef}-${DONATION_HISTORY_REF}` : DONATION_HISTORY_REF;

  // "Donate Now" routes through Oath's shopping-cart donate page, pre-loading just
  // this recipient. The page query_string (p=) is required — without it /donate
  // redirects to /cart — so the link only renders once the query_string has loaded. ref=
  // attributes
  // the checkout to this button; built explicitly (not via getPathWithRef) so an
  // inbound urlRef can't produce a duplicate ref param.
  const donateColumn = useMemo(
    () => ({
      key: 'recipient_id',
      name: '',
      formatFn: (_value, row) =>
        cartQueryString ? (
          <a
            className="button-link"
            href={`/donate?p=${cartQueryString}&shopping-cart=${row.recipient_id}&ref=${donateRef}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackEvent('This Cycle Donate Button Clicked', {
                recipient_id: row.recipient_id,
              })
            }
          >
            Donate Now
          </a>
        ) : null,
    }),
    [cartQueryString, donateRef]
  );

  const cycleColumnsWithDonate = useMemo(() => [...cycleColumns, donateColumn], [donateColumn]);
  const cycleColumnsMobileWithDonate = useMemo(
    () => [...cycleColumnsMobile, donateColumn],
    [donateColumn]
  );

  const getSinceDate = () => {
    const rawDate = summary?.min_donation_date || userData?.created_at;
    return localizedDate(rawDate);
  };

  const onDownloadButtonClick = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    setDownloadError('');
    try {
      await downloadGivingHistory();
    } catch (errorMessage) {
      setDownloadError(errorMessage as string);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      {showGivingHistoryDownload && (
        <>
          <RowContainer $justifyContent="flex-end">
            <Link
              onClick={onDownloadButtonClick}
              disabled={isDownloading}
              $color={theme.colors.inkBlue}
            >
              {isDownloading ? 'Preparing download…' : 'Download your 2026 cycle giving history'}
            </Link>
          </RowContainer>
          {downloadError && (
            <RowContainer $justifyContent="flex-end">
              <UtilityText $color={theme.colors.errorRed}>{downloadError}</UtilityText>
            </RowContainer>
          )}
        </>
      )}
      <Spacing $size={4} />
      <div className="donation-history-summary-row">
        <div className="donation-history-summary-col">
          <h3 className="h3-dtm text-capitalize">{summary?.total_donations || '0'} Donations</h3>
          <p className="body-small-text">Since {getSinceDate()}</p>
        </div>
        <div className="donation-history-summary-col">
          <h3 className="h3-dtm text-capitalize">{summary?.total_candidates || '-'}</h3>
          <p className="body-small-text">Total candidates supported</p>
        </div>
      </div>
      <Spacing $size={16} />
      <div className="donation-history-tabs">
        <button
          type="button"
          className={
            activeTab === 'byDonation'
              ? 'donation-history-tab donation-history-tab-active'
              : 'donation-history-tab'
          }
          onClick={() => onTabChange('byDonation')}
        >
          By Donation
        </button>
        <button
          type="button"
          className={
            activeTab === 'thisCycle'
              ? 'donation-history-tab donation-history-tab-active'
              : 'donation-history-tab'
          }
          onClick={() => {
            onTabChange('thisCycle');
            trackEvent('This Cycle Tab Clicked', {});
          }}
        >
          This Cycle
        </button>
      </div>
      <Spacing $size={16} />
      {activeTab === 'byDonation' ? (
        transactions?.length > 0 ? (
          <TableSection
            columns={columns}
            columnsMobile={columnsMobile}
            rows={transactions ?? []}
            defaultVisibleRowCount={DEFAULT_ROW_COUNT}
          />
        ) : (
          <span className="body-small-text color-black shadow-5">
            Details will appear here after you make your first donation.
          </span>
        )
      ) : contributionList.length > 0 ? (
        <TableSection
          columns={cycleColumnsWithDonate}
          columnsMobile={cycleColumnsMobileWithDonate}
          rows={contributionList}
          defaultVisibleRowCount={DEFAULT_ROW_COUNT}
        />
      ) : (
        <span className="body-small-text color-black shadow-5">
          You haven't donated this cycle yet.
        </span>
      )}
    </>
  );
};
